import React, { useEffect, useRef, useState } from "react";
import {
  BarChart3,
  CheckCircle,
  ChevronRight,
  Clock,
  Inbox,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../api/api";
import toast from "react-hot-toast";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="summary-card bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.10)]">
    <div className="w-12 h-12 rounded-2xl bg-[#06183f]/10 text-[#06183f] flex items-center justify-center mb-4">
      <Icon size={22} />
    </div>

    <p className="text-slate-500 text-sm">{label}</p>
    <h3 className="text-3xl font-black text-[#06183f] mt-1">{value}</h3>
  </div>
);

const ComplaintDataBankPage = () => {
  const pageRef = useRef(null);
  const [data, setData] = useState(null);

  const loadSummary = async () => {
    try {
      const res = await api.get("/complaints/public-summary");
      setData(res.data);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load complaint summary");
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
      });

      gsap.from(".summary-card", {
        opacity: 0,
        y: 55,
        duration: 0.9,
        stagger: 0.12,
        delay: 0.25,
      });

      gsap.from(".category-panel", {
        opacity: 0,
        y: 60,
        duration: 1,
        delay: 0.35,
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, [data]);

  if (!data) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <p className="text-center text-[#06183f] font-bold">
          Loading accountability data...
        </p>
      </main>
    );
  }

  return (
    <main ref={pageRef} className="bg-slate-50 overflow-hidden">
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="Complaint Data Bank"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />

          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />

          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Public Accountability
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Complaint Data Bank
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Individual complaints are protected. Only summary statistics are
              displayed publicly for transparency and accountability.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Complaint Data Bank</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <StatCard
            icon={Inbox}
            label="Complaints Received"
            value={data.total_received}
          />
          <StatCard icon={CheckCircle} label="Resolved" value={data.resolved} />
          <StatCard icon={Clock} label="In Review" value={data.in_review} />
          <StatCard
            icon={BarChart3}
            label="New/Received"
            value={data.received}
          />
        </div>

        <div className="category-panel bg-white rounded-[34px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.10)] p-6 md:p-8">
          <h2
            className="text-3xl md:text-4xl font-black text-[#06183f] mb-6"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Categories of Complaints
          </h2>

          <div className="space-y-4">
            {data.categories.length === 0 ? (
              <p className="text-slate-500">
                No complaints have been submitted yet.
              </p>
            ) : (
              data.categories.map((item) => (
                <div
                  key={item.name}
                  className="border border-slate-100 rounded-2xl p-5 flex justify-between items-center gap-4"
                >
                  <span className="font-bold text-slate-700 break-words">
                    {item.name}
                  </span>
                  <span className="text-2xl font-black text-[#ff6600] shrink-0">
                    {item.count}
                  </span>
                </div>
              ))
            )}
          </div>

          <div className="mt-8 bg-[#06183f]/5 rounded-3xl p-6">
            <h3 className="font-black text-[#06183f] mb-2">
              Response Timeline
            </h3>
            <p className="text-slate-600 leading-relaxed">
              {data.response_timeline}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ComplaintDataBankPage;